/**
 * Currency utilities for dynamic base-currency support.
 *
 * Supported currencies:
 *   - "ILS" (Israeli Shekel ₪) – the default
 *   - "USD" (US Dollar $)
 *   - "EUR" (Euro €)
 *   - "GBP" (British Pound £)
 *
 * Currency preference is persisted in a cookie so that both SSR
 * (`entry.server.tsx`) and client code can read it – mirroring the
 * language cookie pattern in `utils/language.ts`.
 */

export type Currency = "ILS" | "USD" | "EUR" | "GBP";

export const CURRENCIES: Currency[] = ["ILS", "USD", "EUR", "GBP"];

export const CURRENCY_COOKIE_NAME = "moneytor-currency"; // pragma: allowlist secret

export const DEFAULT_CURRENCY: Currency = "ILS";

/** Max-age for the currency cookie – 1 year in seconds. */
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

/** Symbol displayed before/after amounts. */
export const CURRENCY_SYMBOL: Record<Currency, string> = {
  ILS: "₪",
  USD: "$",
  EUR: "€",
  GBP: "£",
};

/** Human-readable label for the currency switcher. */
export const CURRENCY_LABEL: Record<Currency, string> = {
  ILS: "₪ ILS",
  USD: "$ USD",
  EUR: "€ EUR",
  GBP: "£ GBP",
};

/** Short label used for a compact toggle / display. */
export const CURRENCY_SHORT_LABEL: Record<Currency, string> = {
  ILS: "₪",
  USD: "$",
  EUR: "€",
  GBP: "£",
};

/** ISO 4217 code (same as the Currency type, provided for explicitness). */
export const CURRENCY_CODE: Record<Currency, string> = {
  ILS: "ILS",
  USD: "USD",
  EUR: "EUR",
  GBP: "GBP",
};

/** Locale string best suited for formatting each currency. */
export const CURRENCY_LOCALE: Record<Currency, string> = {
  // Use an LTR locale for ILS so the shekel sign stays before the number
  // in RTL screens: "-₪13,938" instead of "-13,938₪".
  ILS: "en-US",
  USD: "en-US",
  EUR: "de-DE",
  GBP: "en-GB",
};

// ---------------------------------------------------------------------------
// Cookie helpers – work on both server (raw header) and client
// ---------------------------------------------------------------------------

/**
 * Read the currency preference from a raw `Cookie` header string (server-side)
 * or from `document.cookie` (client-side).
 */
export function readCurrencyCookie(cookieString: string | null | undefined): Currency {
  if (!cookieString) return DEFAULT_CURRENCY;
  const match = cookieString.match(
    new RegExp(`(?:^|;\\s*)${CURRENCY_COOKIE_NAME}=([^;]+)`)
  );
  const value = match?.[1]?.trim();
  if (isCurrency(value)) return value;
  return DEFAULT_CURRENCY;
}

/**
 * Write the currency cookie on the client (`document.cookie`).
 * Uses `path=/` so it's available on every route, and `SameSite=Lax`.
 */
export function writeCurrencyCookie(currency: Currency): void {
  document.cookie = `${CURRENCY_COOKIE_NAME}=${currency}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/**
 * Format a numeric value as a currency string using the user's chosen
 * base currency.
 *
 * @param value      The numeric amount to format.
 * @param currency   The ISO 4217 currency code (e.g. "ILS", "USD").
 * @param options    Optional overrides for `Intl.NumberFormat`.
 */
/**
 * Compact-notation precision differs by magnitude: millions get 2 decimal
 * places (e.g. ₪3.95M) while thousands only get 1 (e.g. ₪495.2K), so small
 * differences within a K-value don't clutter the display.
 */
function compactFractionDigits(value: number): number {
  const abs = Math.abs(value);
  if (abs >= 1e6) return 2;
  if (abs >= 1e3) return 1;
  return 0;
}

export function formatCurrencyValue(
  value: number,
  currency: Currency = DEFAULT_CURRENCY,
  options?: {
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    compact?: boolean;
    /** Omit minus/plus in output; use color or layout to convey debit vs credit. */
    signDisplay?: Intl.NumberFormatOptions["signDisplay"];
  },
): string {
  const locale = CURRENCY_LOCALE[currency] || "he-IL";
  const formatted = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits:
      options?.maximumFractionDigits ??
      (options?.compact ? compactFractionDigits(value) : 0),
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    ...(options?.signDisplay != null ? { signDisplay: options.signDisplay } : {}),
    ...(options?.compact ? { notation: "compact" } : {}),
  }).format(value);

  // LTR mark prevents the RTL bidi algorithm from moving the minus sign
  // and currency symbol to the wrong side (e.g. "-₪1,457" not "1,457-₪").
  return `\u200E${formatted}`;
}

/**
 * Create a reusable `Intl.NumberFormat` instance for the given currency.
 * For non-ILS currencies the `format` method prepends a LTR mark so the
 * minus sign stays on the correct side in the app's RTL context.
 */
export function createCurrencyFormatter(
  currency: Currency = DEFAULT_CURRENCY,
  options?: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const locale = CURRENCY_LOCALE[currency] || "he-IL";
  const fmt = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    ...options,
  });

  const origFormat = fmt.format.bind(fmt);
  return new Proxy(fmt, {
    get(target, prop, receiver) {
      if (prop === "format") return (value: number) => `\u200E${origFormat(value)}`;
      return Reflect.get(target, prop, receiver);
    },
  });
}

// ---------------------------------------------------------------------------
// Type guard
// ---------------------------------------------------------------------------

function isCurrency(value: string | undefined | null): value is Currency {
  return CURRENCIES.includes(value as Currency);
}
